import * as React from 'react';
import { Bot, User } from 'lucide-react';
import { cn } from '@/shared/lib/cn';

export type MessageRole = 'user' | 'assistant';

export interface MessageProps extends React.HTMLAttributes<HTMLDivElement> {
  role: MessageRole;
}

export interface MessageAvatarProps extends MessageProps {
  /** Ícone da marca (assistente) — se ausente, cai no ícone genérico. */
  src?: string;
}

/** Uma linha da conversa — alinha à direita (usuário) ou esquerda (IA), com avatar. */
export const Message = React.forwardRef<HTMLDivElement, MessageProps>(
  ({ role, className, children, ...props }, ref) => (
    <div
      ref={ref}
      className={cn('flex items-start gap-2.5', role === 'user' && 'flex-row-reverse', className)}
      {...props}
    >
      {children}
    </div>
  ),
);
Message.displayName = 'Message';

export const MessageAvatar = React.forwardRef<HTMLDivElement, MessageAvatarProps>(
  ({ role, src, className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        'flex size-7 shrink-0 items-center justify-center overflow-hidden rounded-full border border-border/70',
        role === 'user' ? 'bg-primary/10 text-primary' : 'bg-foreground/[0.06] text-foreground',
        className,
      )}
      {...props}
    >
      {role === 'assistant' && src ? (
        <img src={src} alt="" className="size-[70%] object-contain" />
      ) : role === 'user' ? (
        <User className="size-3.5" />
      ) : (
        <Bot className="size-3.5" />
      )}
    </div>
  ),
);
MessageAvatar.displayName = 'MessageAvatar';

export const MessageContent = React.forwardRef<HTMLDivElement, MessageProps>(
  ({ role, className, children, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        'w-fit min-w-0 max-w-full rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed shadow-glass-sm',
        role === 'user'
          ? 'rounded-tr-sm bg-primary text-primary-foreground'
          : 'rounded-tl-sm border border-border/70 bg-[hsl(var(--glass-bg)/0.6)] text-foreground backdrop-blur-sm',
        className,
      )}
      {...props}
    >
      {children}
    </div>
  ),
);
MessageContent.displayName = 'MessageContent';
